import React from 'react';

import { Box, Flex, useTheme } from '@chakra-ui/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { format } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Text, Label } from 'recharts';
import { GRID_COLOR_MAPPING } from 'screens/performanceReview/manageAndInsights/ReviewManage/components/ReviewManageAGGrid/ReviewManageAGGridV1';
import { StyledTooltip } from 'styles/common';

import { Avatar } from 'uiToolkitV1/Avatar';
import ColoredGridLayout from 'uiToolkitV1/ColoredGridLayout';
import { Divider } from 'uiToolkitV1/Divider';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

const AxisLabel = ({ axisType, value, viewBox }) => {
  const { x, y, width, height } = viewBox;
  const theme = useTheme();

  const isVert = axisType === 'yAxis';

  const defaultOffset = 26;

  const cx = isVert ? defaultOffset : width / 2 + x;
  const cy = isVert ? height / 2 + y : y + height + defaultOffset;

  return (
    <Text dx={cx} dy={cy} angle={isVert ? -90 : 0} textAnchor="middle" fontSize="12px" fill={theme.colors.text.g2}>
      {value}
    </Text>
  );
};

const CustomXAxisTick = (props) => {
  const {
    textAnchor,
    x,
    y,
    payload: { value },
  } = props;

  return (
    <g>
      <foreignObject className="node" x={x - 25} y={y - 5} width={110} height={20}>
        <StyledTooltip title={value}>
          <text
            className="recharts-text recharts-cartesian-axis-tick-value"
            stroke="none"
            fill="#666"
            textAnchor={textAnchor}
          >
            <TextLabel style={{ color: '#666' }}>{value.length > 15 ? `${value.slice(0, 14)}...` : value}</TextLabel>
          </text>
        </StyledTooltip>
      </foreignObject>
    </g>
  );
};

const CustomYAxisTick = (props) => {
  const {
    textAnchor,
    x,
    y,
    payload: { value },
    formatter,
  } = props;
  const formattedValue = formatter(value).toString();
  const formattedValueLength = formattedValue.length;
  const toShowValue = formattedValueLength <= 25 ? formattedValue : `${formattedValue.slice(0, 22)}...`;

  return (
    <g>
      <foreignObject className="node" x={x - 45} y={y - 10} width={75} height={35}>
        <StyledTooltip title={formattedValue}>
          <text
            className="recharts-text recharts-cartesian-axis-tick-value"
            stroke="none"
            fill="#666"
            textAnchor={textAnchor}
          >
            <TextLabel style={{ color: '#666' }}>{toShowValue}</TextLabel>
          </text>
        </StyledTooltip>
      </foreignObject>
    </g>
  );
};

const GOAL_NAME_PLACEHOLDER = 'GOAL_NAME_PLACEHOLDER';

const replaceGoalModuleNameIfApplicable = (label, moduleName) => label.replace(GOAL_NAME_PLACEHOLDER, moduleName);

const availableForms = ['PERFORMANCE', 'QNA', 'OVERALL', 'POTENTIAL'];

const FORMS_LABEL = {
  PERFORMANCE: 'Performance',
  QNA: 'Q&A',
  OVERALL: 'Overall',
  POTENTIAL: 'Potential',
};

const FORMS_LABEL_1 = {
  PERFORMANCE: `Performance (${GOAL_NAME_PLACEHOLDER} score)`,
  QNA: 'Q&A score',
  OVERALL: 'Overall performance score',
  POTENTIAL: 'Potential (Competency score)',
};

const ScoreLabelValue = ({ label = '', value = '' }) => (
  <Flex direction="column" flexBasis="100%" alignItems="flex-start">
    <TextLabel color="text.g2">{label}</TextLabel>
    <TextBodyMedium bold>{value}</TextBodyMedium>
  </Flex>
);

const TooltipContentV1 = ({ payload }) => {
  const tooltipData = payload?.[0]?.payload?.tooltipData ?? [];
  const reviewCycle = payload?.[0]?.payload?.reviewCycle ?? '';
  const { moduleName } = useOrganisationSettings();

  const singlePersonData = tooltipData?.[0];
  const scoreData = singlePersonData?.score ?? {};
  const scores = Object.keys(scoreData)
    .filter((key) => availableForms.includes(key))
    .map((key) => ({
      key,
      value: scoreData[key],
    }));

  if (!payload?.length || !payload[0]?.payload) return null;

  return (
    <PaperWithShadow p={2}>
      <Box py={1} px={2}>
        <TextBodyMedium bold>Average Score</TextBodyMedium>

        <TextLabel color="text.g2" mt="2">
          Review Cycle
        </TextLabel>
        <TextBodyMedium bold>{reviewCycle}</TextBodyMedium>

        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" mt={3} p={1} gap={4} mb={1}>
          {scores.map(({ key, value }) => (
            <ScoreLabelValue
              key={key}
              label={replaceGoalModuleNameIfApplicable(FORMS_LABEL_1[key], moduleName)}
              value={value}
            />
          ))}
        </Box>
      </Box>

      {tooltipData.map(({ employee }) => {
        const { id, name, imageUrl } = employee;
        return (
          <Flex justify="space-between" p="2px 8px" mt={2} key={id}>
            <Flex>
              <Avatar boxSize="16px" src={imageUrl} />
              <TextBodyMedium bold ml={4}>
                {name}
              </TextBodyMedium>
            </Flex>
          </Flex>
        );
      })}
    </PaperWithShadow>
  );
};

const TooltipContentV2 = ({ payload }) => {
  const tooltipData = payload?.[0]?.payload?.tooltipData ?? [];
  const reviewCycleName = payload?.[0]?.payload?.reviewCycle ?? '';
  const { moduleName } = useOrganisationSettings();

  const singlePersonData = tooltipData?.[0];
  const scoreData = singlePersonData?.score ?? {};

  const scores = Object.keys(scoreData)
    .filter((key) => availableForms.includes(key))
    .map((key) => ({
      key,
      value: scoreData[key].ratingText,
    }));

  const outcomeScoreValue = scoreData?.OUTCOME ? scoreData?.OUTCOME?.ratingText : null;

  const overallScoreValue = scores.find(({ key }) => key === 'OVERALL')?.value;
  const scoresWithoutOverall = scores.filter(({ key }) => key !== 'OVERALL');

  const gridPosition = tooltipData?.[0]?.gridPosition ?? {};
  const gridDefinition = tooltipData?.[0]?.gridDefinition ?? {};
  const { rowCount, colCount } = gridDefinition;
  const { rowNumber, colNumber, color, label } = gridPosition;

  if (!payload[0]?.payload) return null;

  return (
    <PaperWithShadow p={2} ml="210px" mr="-50px">
      <Flex flexDir="column" py={1} px={2} gap={3}>
        <Box>
          <TextLabel color="text.g2">Overall Rating</TextLabel>
          <TextBodyMedium bold>{overallScoreValue}</TextBodyMedium>
        </Box>

        {/* ---------------------------------------------------------------- */}
        {rowCount > 0 && colCount > 0 && (
          <Box>
            <TextLabel color="text.g2" mb={2}>
              Grid Position
            </TextLabel>
            <Flex justifyContent="flex-start" alignItems="center" gap="8px" mb={2}>
              <Box w="28px">
                <ColoredGridLayout
                  rows={Number(rowCount)}
                  name={label}
                  columns={Number(colCount)}
                  selectedRow={Number(rowCount) + 1 - rowNumber || -1}
                  selectedColumn={colNumber || -1}
                  selectedColor={GRID_COLOR_MAPPING[color] || ''}
                />
              </Box>
              <TextLabel>{label}</TextLabel>
            </Flex>
          </Box>
        )}

        {outcomeScoreValue && (
          <Box>
            <TextLabel color="text.g2">Outcome</TextLabel>
            <TextBodyMedium bold>{outcomeScoreValue}</TextBodyMedium>
          </Box>
        )}

        {/* ---------------------------------------------------------------- */}

        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" mt={3} gap={4}>
          {scoresWithoutOverall.map(({ key, value }) => (
            <ScoreLabelValue
              key={key}
              label={replaceGoalModuleNameIfApplicable(FORMS_LABEL_1[key], moduleName)}
              value={value}
            />
          ))}
        </Box>

        {/* ---------------------------------------------------------------- */}

        <Divider color="neutral.F2F2F2" />

        {/* ---------------------------------------------------------------- */}

        <TextLabel color="text.g2">{reviewCycleName}</TextLabel>
      </Flex>
    </PaperWithShadow>
  );
};

const TooltipContent = ({ payload, metaDataKey, metaDataLabelMap }) => {
  const formWiseScore = payload[0]?.payload[metaDataKey] ?? {};
  const formWiseScoreFormattedData = Object.keys(formWiseScore)
    .map((label) => ({
      label,
      value: formWiseScore[label],
    }))
    .filter(({ label }) => label !== 'OVERALL');

  const reviewData = payload?.[0]?.payload;

  return (
    <PaperWithShadow p={2}>
      <Box py={1} px={2}>
        <TextBodyMedium bold>{reviewData?.reviewCycle}</TextBodyMedium>
        {reviewData?.publishedOn && (
          <TextLabel color="text.g2" mt="2px">
            {format(reviewData.publishedOn, 'MMM d, y')}
          </TextLabel>
        )}
      </Box>

      {reviewData?.overallRating && (
        <>
          <Divider color="neutral.F2F2F2" my={1} />
          <Flex justify="space-between" p="2px 8px">
            <TextBodyMedium bold>{metaDataLabelMap.OVERALL}</TextBodyMedium>
            <TextBodyMedium bold ml={4}>
              {reviewData?.overallRating}
            </TextBodyMedium>
          </Flex>
        </>
      )}

      {formWiseScoreFormattedData.length > 0 && (
        <>
          <Divider color="neutral.F2F2F2" my={1} />
          {formWiseScoreFormattedData.map(({ label, value }) => (
            <Flex justify="space-between" p="2px 8px" key={label}>
              <TextBodyMedium>{FORMS_LABEL[label]}</TextBodyMedium>
              <TextBodyMedium ml={4}>{value}</TextBodyMedium>
            </Flex>
          ))}
        </>
      )}
    </PaperWithShadow>
  );
};

const ReviewLineChart = ({
  data = [],
  linesInfo = [
    {
      key: '',
      strokeDasharray: '',
      color: '',
    },
  ],
  xAxisKey = 'ts',
  yAxisKey = '',
  xAxisTickFormatter,
  yAxisTickFormatter,

  xAxisLabel = 'Time',
  xAxisDomain = ['auto', 'auto'],
  yAxisDomain = ['auto', 'auto'],

  yAxisTicks = [],
  yAxisLabel = 'Average Progress',

  xAxisType = 'number',
  yAxisType = 'number',
  metaDataKey,
  metaDataLabelMap,
  tooltipVersion = 'v0',
  tooltipProps = {},
  // this is additive condition change specially used at one place as other logic is messed on
  // how to determine and style the y axis ticks from different places
  yAxisType2 = '',
}) => {
  const theme = useTheme();

  return (
    <ResponsiveContainer>
      <LineChart
        data={data}
        margin={{
          top: 24,
          right: 50,
          left: 60,
          bottom: 50,
        }}
      >
        <CartesianGrid stroke={theme.colors.neutral.F2F2F2} strokeDasharray="3 3" />
        <XAxis
          type={xAxisType}
          dataKey={xAxisKey}
          domain={xAxisDomain}
          tickFormatter={xAxisTickFormatter}
          tick={<CustomXAxisTick />}
          tickSize={0}
          tickMargin={16}
          padding={{ left: 50, right: 50 }}
          stroke="#CCCCCC"
        >
          <Label content={<AxisLabel axisType="xAxis" value={xAxisLabel} />} />
        </XAxis>
        <YAxis
          type={yAxisType}
          tickMargin={yAxisType === 'category' || yAxisType2 === 'category' ? 30 : 0}
          dataKey={yAxisKey}
          ticks={yAxisTicks}
          name="Performance"
          domain={yAxisDomain}
          tick={<CustomYAxisTick formatter={yAxisTickFormatter} />}
          stroke="#CCCCCC"
        >
          <Label content={<AxisLabel axisType="yAxis" value={yAxisLabel} />} />
        </YAxis>
        <Tooltip
          {...tooltipProps}
          content={
            // eslint-disable-next-line no-nested-ternary
            tooltipVersion === 'v0' ? (
              <TooltipContent metaDataKey={metaDataKey} metaDataLabelMap={metaDataLabelMap} />
            ) : tooltipVersion === 'v1' ? (
              <TooltipContentV1 metaDataKey={metaDataKey} metaDataLabelMap={metaDataLabelMap} />
            ) : (
              <TooltipContentV2 metaDataKey={metaDataKey} metaDataLabelMap={metaDataLabelMap} />
            )
          }
        />

        {Array.isArray(linesInfo)
          ? linesInfo.map(({ key, color, strokeDasharray }) => (
              <Line
                connectNulls
                strokeDasharray={strokeDasharray}
                dataKey={key}
                stroke={color}
                dot={{ r: 3, strokeWidth: 1, strokeDasharray: '', fill: color }}
                activeDot={false}
                key={key}
              />
            ))
          : null}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default ReviewLineChart;
